import React, { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useSiteSettings } from "../lib/hooks";
import { buildWhatsAppLink } from "../lib/format";

const Contact = () => {
  const s = useSiteSettings();
  const [f, setF] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || (!f.phone && !f.email)) { toast.error("Please add name and a way to reach you"); return; }
    setBusy(true);
    try { await api.post("/leads", { ...f, source: "contact" }); setDone(true); }
    catch { toast.error("Something went wrong"); }
    finally { setBusy(false); }
  };

  const wa = s?.whatsapp ? buildWhatsAppLink(s.whatsapp, "Hi Autobahn Cars, I'd like to get in touch.") : null;

  return (
    <div className="container-wide py-16" data-testid="page-contact">
      <div className="border-b hairline pb-8 mb-12">
        <div className="overline text-[#7A1519] mb-3">Contact</div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter font-semibold">Visit the showroom.</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <div className="space-y-8">
            <div className="flex items-start gap-4"><MapPin className="text-[#7A1519] mt-1" size={20} />
              <div>
                <div className="overline text-[#242424]/60 mb-1">Address</div>
                <div className="text-[#111111]">{s?.address}</div>
                {s?.google_maps_link && <a href={s.google_maps_link} target="_blank" rel="noreferrer" className="text-xs text-[#7A1519] hover:underline">Get Directions</a>}
              </div>
            </div>
            <div className="flex items-start gap-4"><Phone className="text-[#7A1519] mt-1" size={20} />
              <div><div className="overline text-[#242424]/60 mb-1">Phone</div><a href={`tel:${s?.phone}`} className="text-[#111111]">{s?.phone}</a></div>
            </div>
            <div className="flex items-start gap-4"><MessageCircle className="text-[#7A1519] mt-1" size={20} />
              <div><div className="overline text-[#242424]/60 mb-1">WhatsApp</div>{wa && <a href={wa} target="_blank" rel="noreferrer" className="text-[#111111]">{s?.whatsapp}</a>}</div>
            </div>
            <div className="flex items-start gap-4"><Mail className="text-[#7A1519] mt-1" size={20} />
              <div><div className="overline text-[#242424]/60 mb-1">Email</div><a href={`mailto:${s?.email}`} className="text-[#111111]">{s?.email}</a></div>
            </div>
            <div className="flex items-start gap-4"><Clock className="text-[#7A1519] mt-1" size={20} />
              <div><div className="overline text-[#242424]/60 mb-1">Hours</div><div className="text-[#111111] whitespace-pre-line">{s?.opening_hours}</div></div>
            </div>
          </div>
        </div>

        <div>
          {done ? (
            <div className="bg-white border hairline p-10 rounded-sm text-center" data-testid="contact-success">
              <div className="overline text-[#7A1519]">Message Received</div>
              <div className="font-display text-2xl mt-3 font-semibold">Thanks — we'll be in touch shortly.</div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border hairline p-8 rounded-sm space-y-4">
              <Field label="Name *" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="ct-name" />
              <Field label="Phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testId="ct-phone" />
              <Field label="Email" value={f.email} type="email" onChange={(v) => setF({ ...f, email: v })} testId="ct-email" />
              <div>
                <label className="overline text-[#242424]/60 block mb-2">Message</label>
                <textarea rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#7A1519]" data-testid="ct-message" />
              </div>
              <button type="submit" disabled={busy} className="w-full bg-[#7A1519] hover:bg-[#5a0f12] text-white py-3.5 rounded-sm text-sm font-semibold tracking-wide disabled:opacity-60" data-testid="ct-submit">{busy ? "Sending…" : "Send Message"}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <label className="overline text-[#242424]/60 block mb-2">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#7A1519]" data-testid={testId} />
  </div>
);

export default Contact;
